import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Check,
  Image as ImageIcon,
  LayoutGrid,
  Link as LinkIcon,
  Loader2,
  Trash2,
  Upload,
} from 'lucide-react';
import { imagesApi, libraryImageDisplayName, type UserImage } from '@/api/images.api';
import { LoFormRow, btnSecondary, inputClass } from '@/components/lyrraone/LyrraOneModulePage';
import { unwrapApiData } from '@/lib/store-formation';
import { readApiErrorMessage } from '@/lib/api';
import { toast } from '@/components/ui/Toast';
import { cn } from '@/lib/utils';

type SourceTab = 'url' | 'upload' | 'library';

interface ImageSourcePickerProps {
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
  uploadCategory?: string;
}

export function ImageSourcePicker({
  value,
  onChange,
  disabled,
  uploadCategory = 'site',
}: ImageSourcePickerProps) {
  const { t } = useTranslation();
  const fileInputId = useId();
  const [tab, setTab] = useState<SourceTab>('url');
  const [urlDraft, setUrlDraft] = useState(value);
  const [uploading, setUploading] = useState(false);
  const [libraryImages, setLibraryImages] = useState<UserImage[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);

  useEffect(() => {
    setUrlDraft(value);
  }, [value]);

  const previewSrc = value ? imagesApi.resolveUrl(value) || value : '';

  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true);
    try {
      const res = await imagesApi.list();
      const list = (res as { data?: UserImage[] })?.data ?? (res as UserImage[]);
      setLibraryImages(Array.isArray(list) ? list : []);
    } catch {
      setLibraryImages([]);
    } finally {
      setLibraryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'library') void loadLibrary();
  }, [tab, loadLibrary]);

  const handleUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Sélectionnez un fichier image (JPEG, PNG, WebP…).');
      return;
    }
    setTab('upload');
    setUploading(true);
    try {
      const res = await imagesApi.upload(file, uploadCategory);
      const saved = unwrapApiData<UserImage>(res);
      if (!saved?.url?.trim()) {
        throw new Error('Réponse serveur invalide après import.');
      }
      onChange(saved.url);
      setUrlDraft(saved.url);
      setLibraryImages((prev) => [saved, ...prev.filter((i) => i.id !== saved.id)]);
      toast.success('Image importée dans votre bibliothèque.');
    } catch (error) {
      toast.error(readApiErrorMessage(error, "Impossible d'importer l'image."));
    } finally {
      setUploading(false);
    }
  };

  const tabs: { id: SourceTab; label: string; icon: React.ReactNode }[] = [
    { id: 'url', label: t('eduflow.blockEditor.richText.urlTab', 'URL'), icon: <LinkIcon className="h-3.5 w-3.5" /> },
    { id: 'upload', label: t('eduflow.blockEditor.richText.importTab', 'Importer'), icon: <Upload className="h-3.5 w-3.5" /> },
    { id: 'library', label: t('eduflow.blockEditor.richText.libraryTab', 'Bibliothèque'), icon: <LayoutGrid className="h-3.5 w-3.5" /> },
  ];

  return (
    <div className="space-y-3">
      {previewSrc && (
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800">
          <img src={previewSrc} alt="" className="max-h-40 w-full object-contain" />
          {!disabled && (
            <button
              type="button"
              onClick={() => onChange('')}
              className="absolute right-2 top-2 rounded-lg bg-black/60 p-1.5 text-white hover:bg-black/80"
              aria-label="Retirer l'image"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      <div className="flex gap-1 rounded-lg border border-gray-200 p-1 dark:border-gray-700">
        {tabs.map((item) => (
          <button
            key={item.id}
            type="button"
            disabled={disabled}
            onClick={() => setTab(item.id)}
            className={cn(
              'flex flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1.5 text-xs font-medium transition',
              tab === item.id
                ? 'bg-primary-600 text-white'
                : 'text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
            )}
          >
            {item.icon}
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'url' && (
        <LoFormRow label="URL de l'image">
          <input
            value={urlDraft}
            onChange={(e) => {
              setUrlDraft(e.target.value);
              onChange(e.target.value);
            }}
            className={inputClass}
            placeholder="https://… ou /uploads/…"
            disabled={disabled}
          />
        </LoFormRow>
      )}

      {tab === 'upload' && (
        <label
          htmlFor={fileInputId}
          className={cn(
            'flex min-h-[140px] cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-200 bg-gray-50 transition hover:border-primary-400 dark:border-gray-700 dark:bg-gray-900',
            (disabled || uploading) && 'pointer-events-none opacity-60',
          )}
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          ) : (
            <>
              <ImageIcon className="h-8 w-8 text-gray-400" />
              <p className="text-center text-sm text-gray-500">
                Glissez une image ou cliquez pour l&apos;ajouter à votre bibliothèque
              </p>
            </>
          )}
          <input
            id={fileInputId}
            type="file"
            accept="image/*"
            className="sr-only"
            disabled={disabled || uploading}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => {
              e.stopPropagation();
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void handleUpload(file);
            }}
          />
        </label>
      )}

      {tab === 'library' && (
        <div className="min-h-[160px]">
          {libraryLoading ? (
            <div className="flex h-40 items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
            </div>
          ) : libraryImages.length === 0 ? (
            <div className="flex h-40 flex-col items-center justify-center gap-2 text-center text-sm text-gray-500">
              <p>Aucune image dans votre bibliothèque.</p>
              <button type="button" className={btnSecondary} onClick={() => setTab('upload')}>
                Importer une image
              </button>
            </div>
          ) : (
            <div className="grid max-h-56 grid-cols-3 gap-2 overflow-y-auto sm:grid-cols-4">
              {libraryImages.map((img) => {
                const selected = value === img.url || imagesApi.resolveUrl(value) === imagesApi.resolveUrl(img.url);
                const displayName = libraryImageDisplayName(img);
                return (
                  <button
                    key={img.id}
                    type="button"
                    title={displayName}
                    disabled={disabled}
                    onClick={() => {
                      onChange(img.url);
                      setUrlDraft(img.url);
                    }}
                    className={cn(
                      'group relative aspect-square overflow-hidden rounded-lg border-2 transition',
                      selected
                        ? 'border-primary-600 ring-2 ring-primary-200'
                        : 'border-transparent hover:border-gray-300 dark:hover:border-gray-600',
                    )}
                  >
                    <img
                      src={imagesApi.resolveUrl(img.url)}
                      alt={displayName}
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                    {selected && (
                      <span className="absolute inset-0 flex items-center justify-center bg-primary-900/30">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-primary-600 text-white">
                          <Check className="h-3.5 w-3.5" />
                        </span>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function resolveSiteImageUrl(url?: string | null): string {
  if (!url?.trim()) return '';
  return imagesApi.resolveUrl(url.trim()) || url.trim();
}
